import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { TERMOS } from '../lib/glossario'
import { IconeAjuda } from './Icones'

/**
 * As chaves do glossário que de fato têm `ajuda`.
 *
 * O tipo cobra o resto: escrever `<Explicacao termo="chamado" />` compila, e um termo sem ajuda
 * escrita nem aparece na lista do editor.
 */
export type ChaveComAjuda = {
  [K in keyof typeof TERMOS]: 'ajuda' extends keyof (typeof TERMOS)[K] ? K : never
}[keyof typeof TERMOS]

interface Props {
  /** A chave do glossário. Rótulo e texto saem de `TERMOS` — nunca se escreve o texto aqui. */
  termo?: ChaveComAjuda
  /** Alternativa, para o que não é entrada de glossário. Exige `texto`. */
  titulo?: string
  texto?: ReactNode
  className?: string
}

/**
 * A interrogação que explica um termo técnico, ao lado dele.
 *
 * **Existe porque o glossário tem dezenas de explicações que nunca chegavam à tela.** "ICA",
 * "IAP", "TIS", "RDM", "rollback" apareciam crus, e a frase que os traduz estava escrita e
 * guardada — trabalho pronto e desperdiçado. O público são fiscais de contrato e servidores que
 * não são de TI: uma sigla sem tradução é uma tela que só quem já sabe consegue ler.
 *
 * **Clique, não passar o mouse.** Duas razões, e as duas são de uso real: no celular não existe
 * passar o mouse, e o fiscal confere pelo telefone; e balão que só aparece no hover é invisível
 * para quem navega por teclado. O gatilho é um `<button>` de verdade, então Tab, Enter e Espaço
 * funcionam sem código nenhum.
 *
 * **Não é modal.** Sem overlay, sem prender o foco: é um parágrafo curto, e prender o foco em
 * duas linhas de texto atrapalha quem usa teclado. Esc fecha e devolve o foco à interrogação;
 * clicar fora também fecha.
 *
 * Fechado, o conteúdo não existe no DOM — escondê-lo por CSS deixaria o Tab entrar num texto
 * invisível.
 *
 * @param props o termo do glossário, ou um título e texto próprios
 * @returns a interrogação com a explicação
 */
export function Explicacao({ termo, titulo, texto, className }: Props) {
  const [aberto, definirAberto] = useState(false)
  const caixa = useRef<HTMLSpanElement>(null)
  const gatilho = useRef<HTMLButtonElement>(null)
  const base = useId()
  const idDoBalao = `${base}-explicacao`

  const doGlossario = termo ? TERMOS[termo] : undefined
  const rotulo = titulo ?? doGlossario?.rotulo ?? ''
  const conteudo = texto ?? (doGlossario && 'ajuda' in doGlossario ? doGlossario.ajuda : '')

  useEffect(() => {
    if (!aberto) {
      return
    }
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'Escape') {
        evento.stopPropagation()
        definirAberto(false)
        gatilho.current?.focus()
      }
    }
    function aoApontar(evento: PointerEvent) {
      if (evento.target instanceof Node && !caixa.current?.contains(evento.target)) {
        definirAberto(false)
      }
    }
    // `capture` no Esc pela mesma razão do Dialogo: sem ele, um diálogo por fora fecharia junto.
    document.addEventListener('keydown', aoTeclar, { capture: true })
    document.addEventListener('pointerdown', aoApontar)
    return () => {
      document.removeEventListener('keydown', aoTeclar, { capture: true })
      document.removeEventListener('pointerdown', aoApontar)
    }
  }, [aberto])

  return (
    <span ref={caixa} className="relative inline-flex align-middle">
      <button
        ref={gatilho}
        type="button"
        aria-expanded={aberto}
        aria-controls={idDoBalao}
        onClick={() => definirAberto((valor) => !valor)}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-full text-slate-400',
          'transition-colors hover:text-niteroi-700 focus-visible:outline focus-visible:outline-2',
          'focus-visible:outline-offset-2 focus-visible:outline-niteroi-600',
        )}
      >
        {/* O ícone fica `aria-hidden` e quem nomeia o botão é o texto abaixo: com os dois, o
            leitor de tela anunciaria a mesma coisa duas vezes. */}
        <IconeAjuda className={cn('h-4 w-4', className)} />
        <span className="sr-only">O que significa {rotulo}?</span>
      </button>

      {aberto && (
        <span
          id={idDoBalao}
          role="note"
          className={
            'absolute left-0 top-full z-20 mt-1.5 w-72 rounded-lg border border-slate-300 bg-white p-3 ' +
            'text-left text-xs font-normal leading-relaxed text-slate-700 shadow-cartao-alto'
          }
        >
          <strong className="block font-semibold text-slate-900">{rotulo}</strong>
          <span className="mt-1 block">{conteudo}</span>
        </span>
      )}
    </span>
  )
}
